import { Injectable } from '@angular/core';
import { UserPreferences } from 'app/trello/models/user-preferences';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserPreferenceStore {

  private readonly defaultPreferences: UserPreferences = {
    sideExpanded: true,
  };

  private readonly storageKey = 'userPreferences';

  private preferencesSubject: BehaviorSubject<UserPreferences>;

  public preferences: Observable<UserPreferences>;

  constructor() {

    const initialPreferences = this.loadPreferences();

    this.preferencesSubject = new BehaviorSubject<UserPreferences>(initialPreferences);

    this.preferences = this.preferencesSubject.asObservable();
  }

  public get userPrefersSideExpanded(): boolean {
    return this.preferencesSubject.value.sideExpanded;
  }

  public set userPrefersSideExpanded(
    sideExpanded: boolean,
  ) {

    if (this.preferencesSubject.value.sideExpanded === sideExpanded) {
      return;
    }

    const updatedPreferences = {
      ...this.preferencesSubject.value,
      sideExpanded: sideExpanded,
    } as UserPreferences;

    setTimeout(() => {

      this.savePreferences(updatedPreferences);

      this.preferencesSubject.next(updatedPreferences);
    });
  }

  private loadPreferences(): UserPreferences {

    if (!this.localStorageAvailable()) {
      return this.defaultPreferences;
    }

    const json = localStorage.getItem(this.storageKey);

    if (!json) {
      return this.defaultPreferences;
    }

    try {

      const unsafePreferences = JSON.parse(json);

      return {
        sideExpanded: !!unsafePreferences.sideExpanded,
      };

    } catch (error) {

      console.error('Error parsing user preferences from local storage:', error);
      return this.defaultPreferences;
    }
  }

  private savePreferences(
    preferences: UserPreferences
  ): void {

    if (!this.localStorageAvailable()) {

      console.warn('Local storage is not available. User preferences will not be saved.');
      return;
    }

    try {

      const json = JSON.stringify(preferences);

      localStorage.setItem(this.storageKey, json);

    } catch (error) {
      console.error('Error saving user preferences to local storage:', error);
    }
  }

  private localStorageAvailable(): boolean {

    try {

      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;

    } catch (e) {
      return false;
    }
  }

}
